import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Stack, Text, TextInput } from '@mantine/core';
import { requestMagicLink } from '../api/auth';
import { getBrowserSessionId } from '../auth/browserSession';

interface RequestLinkFormProps {
  description: string;
  submitLabel?: string;
  successMessage?: string;
  returnTo?: string;
  onRequested?: () => void;
}

export default function RequestLinkForm({
  description,
  submitLabel = 'Send Sign-In Link',
  successMessage = "If your email is authorized, you'll receive a sign-in link shortly.",
  returnTo,
  onRequested,
}: RequestLinkFormProps) {
  const [email, setEmail] = useState('');
  const mutation = useMutation({
    mutationFn: ({ email, browserSessionId }: { email: string; browserSessionId: string }) => requestMagicLink(email, browserSessionId, returnTo),
    onSuccess: () => {
      onRequested?.();
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!event.currentTarget.reportValidity()) {
      return;
    }

    await mutation.mutateAsync({ email, browserSessionId: getBrowserSessionId() });
  };

  return (
    <form onSubmit={(event) => { void handleSubmit(event); }}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">{description}</Text>
        <TextInput
          type="email"
          label="Work Email"
          placeholder="name@company.com"
          required
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          autoComplete="email"
        />
        <Button
          type="submit"
          loading={mutation.isPending}
          className="portal-primary-button"
        >
          {submitLabel}
        </Button>
        {mutation.isSuccess && (
          <Alert color="green" title="Check your email">
            {successMessage}
          </Alert>
        )}
        {mutation.isError && (
          <Alert color="red" title="Unable to send sign-in link">
            Please try again in a moment.
          </Alert>
        )}
      </Stack>
    </form>
  );
}